import { Banknote, TrendingUp, TrendingDown, Landmark, ReceiptText, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { DateRangeSelect, useHubData, fmtUSD, rangeQuery, CardError } from './DateRangeSelect';

const METRICS = [
  { key: 'revenue', metric: 'revenue', title: 'Total Revenue', icon: Banknote, iconCls: 'text-[#118A35]', bgCls: 'bg-[#20B94B]/10', drill: '/reports?report=profit-loss' },
  { key: 'net_profit', metric: 'net_profit', title: 'Net Profit', icon: TrendingUp, iconCls: 'text-[#118A35]', bgCls: 'bg-[#20B94B]/10', drill: '/reports?report=profit-loss' },
  { key: 'total_cash', metric: 'total_cash', title: 'Total Cash', icon: Landmark, iconCls: 'text-[#2979F2]', bgCls: 'bg-[#2979F2]/10', drill: '/banking' },
  { key: 'outstanding_invoices', metric: 'outstanding_invoices', title: 'Outstanding Invoices', icon: ReceiptText, iconCls: 'text-[#FF7816]', bgCls: 'bg-[#FF7816]/10', drill: '/invoices' },
];

function KpiCard({ def, wsId, range, onRangeChange }) {
  const navigate = useNavigate();
  const url = wsId ? `/api/dashboard-hub/kpis?workspace_id=${wsId}&metric=${def.metric}&${rangeQuery(range)}` : null;
  const { data, loading, error, retry } = useHubData(url);
  const Icon = def.icon;
  const change = data?.pct_change;
  const positive = typeof change === 'number' && change >= 0;

  return (
    <article
      className="min-w-0 rounded-2xl border border-[#E5EAF0] bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
      data-testid={`kpi-card-${def.key}`}
    >
      <div className="flex items-center justify-between gap-2">
        <DateRangeSelect value={range} onChange={onRangeChange} ariaLabel={`Date range for ${def.title}`} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label={`More options for ${def.title}`} data-testid={`kpi-menu-${def.key}`}
              className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-50 hover:text-slate-600">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => navigate(def.drill)}>View details</DropdownMenuItem>
            <DropdownMenuItem onSelect={retry}>Refresh</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="mt-4 flex min-w-0 items-center gap-2.5">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${def.bgCls}`}>
          <Icon className={`h-5 w-5 ${def.iconCls}`} aria-hidden="true" />
        </span>
        <p className="truncate text-sm font-medium text-[#657085]">{def.title}</p>
      </div>
      <div className="mt-3">
        {loading ? (
          <div className="h-9 w-32 animate-pulse rounded-lg bg-slate-100" data-testid={`kpi-loading-${def.key}`} />
        ) : error ? (
          <CardError onRetry={retry} />
        ) : (
          <>
            <p className={`text-2xl font-bold tracking-tight sm:text-3xl ${data.value < 0 ? 'text-[#EF4444]' : 'text-[#172033]'}`}
              data-testid={`kpi-value-${def.key}`}>
              {fmtUSD(data.value)}
              {def.key === 'outstanding_invoices' && typeof data.invoice_count === 'number' && (
                <span className="ml-2 text-xs font-medium text-[#657085]">{data.invoice_count} open</span>
              )}
            </p>
            <div className="mt-2.5 flex items-center gap-1.5 text-xs" data-testid={`kpi-change-${def.key}`}>
              {!data.has_data ? (
                <span className="text-[#657085]">No data available for this period</span>
              ) : change === null || change === undefined ? (
                <span className="text-[#657085]">No prior-period comparison</span>
              ) : (
                <>
                  {positive
                    ? <TrendingUp className="h-4 w-4 text-[#118A35]" aria-hidden="true" />
                    : <TrendingDown className="h-4 w-4 text-[#EF4444]" aria-hidden="true" />}
                  <span className={positive ? 'font-semibold text-[#118A35]' : 'font-semibold text-[#EF4444]'}>
                    {Math.abs(change).toFixed(1)}%
                  </span>
                  <span className="text-[#657085]">vs prior period</span>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </article>
  );
}

export default function KpiGrid({ wsId, ranges, updateRange }) {
  return (
    <section aria-label="Key financial metrics" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="kpi-grid">
      {METRICS.map((def) => (
        <KpiCard key={def.key} def={def} wsId={wsId}
          range={ranges[def.key]} onRangeChange={(r) => updateRange(def.key, r)} />
      ))}
    </section>
  );
}
